import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, CheckCircle2, AlertTriangle, Paperclip } from "lucide-react";
import { Btn, Modal, Select } from "@/components/Primitives";
import { api, fmtCurrency, formatApiErrorDetail, saveBlob } from "@/lib/api";
import { toast } from "sonner";

const CLASS_LABEL = { startup: "Start-up", variable: "Variable", fixed: "Fixed", consumables: "Consumables", transport: "Transport", fee: "Fee" };

export function CostImportModal({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setPreview(null); }, [open]);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/finance/cost-import/template.xlsx", { responseType: "blob" });
      saveBlob(r, "cost-import-template.xlsx");
    } catch { toast.error("Could not download template"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/finance/cost-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finance/cost-import/commit", { rows: preview.rows });
      toast.success(`${r.data.created} cost(s) imported · ${fmtCurrency(r.data.total, "ZAR")}`);
      onImported?.(r.data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const total = (preview?.rows || []).filter((r) => !r.errors.length).reduce((a, r) => a + (r.amount || 0), 0);

  return (
    <Modal open={open} onClose={onClose} title="Import costs from Excel" size="xl" testid="cost-import-modal"
           footer={<>
             <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={commit} disabled={busy || !preview || preview.invalid > 0 || preview.valid === 0} testid="cost-import-commit">
               Import {preview?.valid || 0} cost(s)
             </Btn>
           </>}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Btn variant="secondary" onClick={downloadTemplate} testid="cost-import-template">
          <Download className="h-4 w-4" /> Download template
        </Btn>
        <Btn variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy} testid="cost-import-pick">
          <Upload className="h-4 w-4" /> {busy ? "Checking..." : "Upload filled template"}
        </Btn>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="cost-import-file" />
        <span className="text-xs text-stone-500">Rows are checked first — nothing is saved until you confirm.</span>
      </div>
      {preview && (
        <>
          <div className={`text-sm mb-2 flex items-center gap-2 ${preview.invalid ? "text-amber-800" : "text-emerald-800"}`} data-testid="cost-import-summary">
            {preview.invalid ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {preview.valid} valid row(s) · {preview.invalid} with errors · total {fmtCurrency(total, "ZAR")}
            {preview.invalid > 0 && <span className="text-xs">— fix the red rows in your sheet and upload again</span>}
          </div>
          <div className="overflow-auto max-h-[55vh] border border-stone-200 rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-stone-100 sticky top-0">
                <tr>{["Row", "Date", "Category", "Supplier", "Customer", "Description", "Qty", "Rate", "Amount", "Tags", "Attachments", "Status"].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-600 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} className={r.errors.length ? "bg-red-50" : "odd:bg-white even:bg-stone-50/60"} data-testid={`cost-import-row-${r.row}`}>
                    <td className="px-2 py-1 font-mono">{r.row}</td>
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{r.cost_date || "—"}</td>
                    <td className="px-2 py-1">{r.category || "—"}</td>
                    <td className="px-2 py-1">{r.supplier_name || "—"}</td>
                    <td className="px-2 py-1">{r.customer_name || "—"}</td>
                    <td className="px-2 py-1 max-w-[260px] truncate" title={r.description}>{r.description || "—"}</td>
                    <td className="px-2 py-1 text-right font-mono">{r.quantity}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtCurrency(r.unit_price, "ZAR")}</td>
                    <td className="px-2 py-1 text-right font-mono">{fmtCurrency(r.amount, "ZAR")}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{r.class_split_amounts ? Object.entries(r.class_split_amounts).map(([k, v]) => `${CLASS_LABEL[k]} ${fmtCurrency(v, "ZAR")}`).join(", ") : "—"}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{r.attachment_names.length ? r.attachment_names.join("; ") : "—"}</td>
                    <td className="px-2 py-1 text-red-700 min-w-[260px]">{r.errors.length ? r.errors.join("; ") : <span className="text-emerald-700">OK</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

export function BulkAttachModal({ open, onClose, onDone }) {
  const fileRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [result, setResult] = useState(null);
  const [leftover, setLeftover] = useState([]); // File objects not matched by name
  const [assign, setAssign] = useState({});
  const [costs, setCosts] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPending = () => api.get("/finance/cost-import/pending-attachments").then((r) => setPending(r.data)).catch(() => {});
  useEffect(() => {
    if (!open) { setResult(null); setLeftover([]); setAssign({}); return; }
    loadPending();
    api.get("/finance/costs", { params: { status: "uninvoiced" } }).then((r) => setCosts(r.data)).catch(() => {});
  }, [open]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/finance/cost-import/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      setLeftover(files.filter((f) => r.data.unmatched.includes(f.name)));
      toast.success(`${r.data.matched.length} file link(s) made`);
      loadPending();
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const assignOne = async (file) => {
    const cid = assign[file.name];
    if (!cid) { toast.error("Pick a cost first"); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("linked_to", "cost");
      fd.append("linked_id", cid);
      fd.append("category", "cost");
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached`);
      setLeftover((p) => p.filter((f) => f.name !== file.name));
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Bulk attach supplier documents" size="lg" testid="bulk-attach-modal"
           footer={<Btn variant="secondary" onClick={onClose}>Close</Btn>}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Btn onClick={() => fileRef.current?.click()} disabled={busy} testid="bulk-attach-pick">
          <Upload className="h-4 w-4" /> {busy ? "Uploading..." : "Select files"}
        </Btn>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="bulk-attach-files" />
        <span className="text-xs text-stone-500">Each file is linked only to the cost row(s) whose "Attachment file(s)" names it.</span>
      </div>

      {pending.length > 0 && (
        <div className="mb-3 panel p-3 bg-amber-50/60 border-amber-200" data-testid="bulk-attach-pending">
          <div className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Still waiting for files ({pending.length} cost(s))</div>
          <ul className="text-xs text-stone-700 max-h-32 overflow-auto space-y-0.5">
            {pending.map((p) => <li key={p.id}><span className="font-mono">{p.cost_date}</span> · {p.description} · {fmtCurrency(p.amount, "ZAR")} → <span className="font-mono">{p.missing.join("; ")}</span></li>)}
          </ul>
        </div>
      )}

      {result && (
        <div className="space-y-3 text-sm" data-testid="bulk-attach-result">
          <div className="text-emerald-800 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> {result.matched.length} linked
            {result.already_attached.length > 0 && <span className="text-stone-500"> · {result.already_attached.length} already attached (skipped)</span>}
          </div>
          {result.matched.length > 0 && (
            <ul className="text-xs text-stone-700 max-h-40 overflow-auto space-y-0.5">
              {result.matched.map((m, i) => <li key={i}><span className="font-mono">{m.filename}</span> → {m.description} ({fmtCurrency(m.amount, "ZAR")})</li>)}
            </ul>
          )}
          {leftover.length > 0 && (
            <div className="panel p-3 border-amber-200" data-testid="bulk-attach-unmatched">
              <div className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {leftover.length} file(s) matched no cost — assign manually</div>
              <div className="space-y-2">
                {leftover.map((f) => (
                  <div key={f.name} className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono min-w-[160px]">{f.name}</span>
                    <Select value={assign[f.name] || ""} onChange={(e) => setAssign((p) => ({ ...p, [f.name]: e.target.value }))} className="max-w-[360px]" data-testid={`bulk-attach-assign-${f.name}`}>
                      <option value="">Pick a cost…</option>
                      {costs.map((c) => <option key={c.id} value={c.id}>{c.cost_date} · {c.description} · {fmtCurrency(c.amount, "ZAR")}</option>)}
                    </Select>
                    <Btn variant="secondary" onClick={() => assignOne(f)} testid={`bulk-attach-do-${f.name}`}>Attach</Btn>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
